import type { Category } from '../types';

export const safety: Category = {
  slug: "safety-on-deliveries",
  half: "work",
  accent: '#D93025',
  icon: "flag",
  label: { en: "Safety on deliveries", es: "Seguridad en las entregas" },
  blurb: {
    en: "Night deliveries, dogs, parking, lifting, weather — and the rule that never changes: 911 first, always.",
    es: "Entregas de noche, perros, estacionamiento, cargar peso, clima, y la regla que nunca cambia: primero el 911, siempre.",
  },
  guides: [
    {
      slug: "night-deliveries",
      related: [
        "reporting-a-problem/customer-issue",
        "reporting-a-problem/what-never-counts-against-you",
        "your-gear/phone-mount-and-power",
      ],
      body: {
        en: {
          title: "Night deliveries",
          summary: "Light, awareness and a plan for the walk from the car to the door.",
          sections: [
            {
              heading: "Before you leave the store",
              paragraphs: [
                "Check the customer's delivery notes for a gate code, apartment number or \"leave at the side door\" instructions. Reading them in the parking lot beats reading them in the dark. Confirm your phone is charged and mounted.",
              ],
            },
            {
              heading: "At the address",
              bullets: [
                "Park where you can see both the door and the car, in light if there is any.",
                "Leave the headlights on until you've found the entrance.",
                "Carry a small flashlight; your phone light drains the battery the app needs.",
                "Take the well-lit path even if it's longer.",
                "Keep one hand free.",
              ],
            },
            {
              heading: "If something feels off",
              paragraphs: [
                "You never have to complete a stop that feels unsafe. Stay in the car, leave, and mark the stop as an exception in the app. That never counts against you.",
                "If you have a safety concern about a customer, an address or a vendor, report it in the app — from the run screen or the portal — and Grocery List Plus handles it. A Runner never confronts anyone at a door.",
              ],
            },
            {
              heading: "The rule that never changes",
              paragraphs: ["In any emergency, call 911 first, always. Report to Grocery List Plus afterward."],
            },
          ],
          actions: [
            "Put a flashlight in the car this week.",
            "Read the delivery notes before you drive to the address, every stop.",
            "Find \"report a problem\" on the run screen so you know where it is before you need it.",
          ],
        },
        es: {
          title: "Entregas de noche",
          summary: "Luz, atención y un plan para el trayecto del auto a la puerta.",
          sections: [
            {
              heading: "Antes de salir de la tienda",
              paragraphs: [
                "Revisa las notas de entrega del cliente: código de portón, número de apartamento o instrucciones como \"dejar en la puerta lateral\". Leerlas en el estacionamiento es mejor que leerlas a oscuras. Confirma que tu teléfono esté cargado y en el soporte.",
              ],
            },
            {
              heading: "En la dirección",
              bullets: [
                "Estaciona donde puedas ver la puerta y el auto, con luz si la hay.",
                "Deja los faros encendidos hasta encontrar la entrada.",
                "Lleva una linterna pequeña; la luz del teléfono gasta la batería que la app necesita.",
                "Toma el camino iluminado aunque sea más largo.",
                "Mantén una mano libre.",
              ],
            },
            {
              heading: "Si algo no se siente bien",
              paragraphs: [
                "Nunca tienes que completar una parada que se sienta insegura. Quédate en el auto, vete y marca la parada como excepción en la app. Eso nunca cuenta en tu contra.",
                "Si tienes una preocupación de seguridad sobre un cliente, una dirección o un vendedor, repórtala en la app, desde la pantalla de la ruta o el portal, y Lista de Compras Plus se encarga. Un Runner nunca confronta a nadie en una puerta.",
              ],
            },
            {
              heading: "La regla que nunca cambia",
              paragraphs: ["En cualquier emergencia, llama primero al 911, siempre. Reporta a Lista de Compras Plus después."],
            },
          ],
          actions: [
            "Pon una linterna en el auto esta semana.",
            "Lee las notas de entrega antes de manejar a la dirección, en cada parada.",
            "Ubica \"reportar un problema\" en la pantalla de la ruta para saber dónde está antes de necesitarlo.",
          ],
        },
      },
    },
    {
      slug: "dogs-and-animals",
      related: [
        "reporting-a-problem/customer-issue",
        "reporting-a-problem/what-never-counts-against-you",
        "if-you-have-an-accident/incident-checklist",
      ],
      body: {
        en: {
          title: "Dogs and other animals",
          summary: "Most dogs are fine. Plan for the ones that aren't.",
          sections: [
            {
              heading: "Read the notes, then the yard",
              paragraphs: [
                "Customers can mention pets in delivery notes, so read them. At the address, look for a dog before you open a gate: a loose dog in the yard, a \"beware of dog\" sign, barking from behind the door. A dog you can hear but not see is the one to plan around.",
              ],
            },
            {
              heading: "At the door",
              bullets: [
                "Don't enter a fenced yard with a loose dog. Leave the order at the gate if the notes allow it, or message the customer from the app.",
                "Keep the bags between you and the dog; never reach over one.",
                "Don't run, don't stare, don't reach out to pet.",
                "Stay calm and back away slowly toward the car if it approaches.",
              ],
            },
            {
              heading: "If you're bitten",
              paragraphs: [
                "Get to safety. If you need medical help, call 911 first, always. Then report it in the app; Grocery List Plus handles the follow-up with the customer. Photograph the bite and note the time. See a doctor even for a small bite — dog bites get infected.",
              ],
            },
            {
              heading: "Leaving is always allowed",
              paragraphs: [
                "If an animal makes the stop unsafe, leave and mark the stop as an exception in the app. It never counts against you. Never argue with a customer about their pet — report it in the app and let Grocery List Plus sort it out.",
              ],
            },
          ],
          actions: [
            "Check the delivery notes for pet mentions on every stop this week.",
            "Decide now what you'll do if there's a loose dog: gate drop, message, or leave.",
            "Keep a small first-aid kit in the car.",
          ],
        },
        es: {
          title: "Perros y otros animales",
          summary: "La mayoría de los perros no dan problema. Planea para los que sí.",
          sections: [
            {
              heading: "Lee las notas y luego el patio",
              paragraphs: [
                "Los clientes pueden mencionar mascotas en las notas de entrega, así que léelas. En la dirección, busca un perro antes de abrir un portón: un perro suelto en el patio, un letrero de \"cuidado con el perro\", ladridos detrás de la puerta. El perro que oyes pero no ves es el que hay que tener en cuenta.",
              ],
            },
            {
              heading: "En la puerta",
              bullets: [
                "No entres a un patio cercado con un perro suelto. Deja el pedido en el portón si las notas lo permiten, o escríbele al cliente desde la app.",
                "Mantén las bolsas entre tú y el perro; nunca extiendas el brazo por encima de uno.",
                "No corras, no lo mires fijo, no intentes acariciarlo.",
                "Mantén la calma y retrocede despacio hacia el auto si se acerca.",
              ],
            },
            {
              heading: "Si te muerden",
              paragraphs: [
                "Ponte a salvo. Si necesitas atención médica, llama primero al 911, siempre. Luego repórtalo en la app; Lista de Compras Plus se encarga del seguimiento con el cliente. Fotografía la mordida y anota la hora. Ve al médico aunque sea pequeña: las mordidas de perro se infectan.",
              ],
            },
            {
              heading: "Irte siempre está permitido",
              paragraphs: [
                "Si un animal hace insegura la parada, vete y márcala como excepción en la app. Nunca cuenta en tu contra. Nunca discutas con un cliente sobre su mascota: repórtalo en la app y deja que Lista de Compras Plus lo resuelva.",
              ],
            },
          ],
          actions: [
            "Revisa las notas de entrega buscando mascotas en cada parada de esta semana.",
            "Decide desde ahora qué harás si hay un perro suelto: dejar en el portón, escribir o irte.",
            "Ten un botiquín pequeño en el auto.",
          ],
        },
      },
    },
    {
      slug: "parking-and-walking-routes",
      related: [
        "your-gear/cart-and-winter-gear",
        "deductions/tolls-and-parking",
        "reporting-a-problem/what-never-counts-against-you",
      ],
      body: {
        en: {
          title: "Parking and walking routes",
          summary: "Where you park decides how safe the next five minutes are.",
          sections: [
            {
              heading: "Parking that works",
              bullets: [
                "Park legally. A ticket is your expense and a tow ends the shift.",
                "Pull in so you can pull out forward. Backing out with bags is when mirrors get clipped.",
                "Don't block driveways, hydrants or fire lanes, even for two minutes.",
                "In lots, park near the entrance and under a light.",
                "Lock the car every time, even for a 30-second drop.",
              ],
            },
            {
              heading: "The walk",
              paragraphs: [
                "Pick the route you can see, not the shortest one. Use sidewalks and marked crossings, and walk facing traffic where there's no sidewalk. Keep your phone in a pocket and your eyes up — check the app before you get out, not while you walk. Two trips with a comfortable load beat one trip you can't see over.",
              ],
            },
            {
              heading: "Apartments and buildings",
              paragraphs: [
                "Read the notes for building access. Don't prop doors or follow residents in without permission. If you can't get in, message the customer from the app. If there's no answer, follow the in-app instructions and mark the stop as an exception rather than waiting in a stairwell.",
              ],
            },
            {
              heading: "If you feel unsafe",
              paragraphs: [
                "Leave. Mark the stop as an exception in the app; it never counts against you. Report any concern about an address in the app and Grocery List Plus handles it — a Runner never confronts anyone. In an emergency, call 911 first, always.",
              ],
            },
          ],
          actions: [
            "Practice pull-through parking on your next three runs.",
            "Use a cart for anything more than two bags — see Your gear.",
            "Find the building-access field in the delivery notes so you know where to look.",
          ],
        },
        es: {
          title: "Estacionamiento y rutas a pie",
          summary: "Dónde estacionas decide qué tan seguros son los siguientes cinco minutos.",
          sections: [
            {
              heading: "Estacionar bien",
              bullets: [
                "Estaciona legalmente. Una multa es tu gasto y una grúa termina el turno.",
                "Entra de forma que puedas salir de frente. Al salir en reversa con bolsas es cuando se rompen los espejos.",
                "No bloquees entradas, hidrantes ni carriles de emergencia, ni por dos minutos.",
                "En estacionamientos, quédate cerca de la entrada y bajo una luz.",
                "Cierra el auto con llave cada vez, aunque la entrega tome 30 segundos.",
              ],
            },
            {
              heading: "El trayecto a pie",
              paragraphs: [
                "Elige la ruta que puedes ver, no la más corta. Usa banquetas y cruces marcados, y camina de frente al tráfico donde no haya banqueta. Guarda el teléfono en el bolsillo y mantén la vista al frente: revisa la app antes de bajarte, no mientras caminas. Dos viajes con una carga cómoda son mejores que uno con bolsas que te tapan la vista.",
              ],
            },
            {
              heading: "Apartamentos y edificios",
              paragraphs: [
                "Lee las notas sobre el acceso al edificio. No dejes puertas abiertas ni entres detrás de residentes sin permiso. Si no puedes entrar, escríbele al cliente desde la app. Si no responde, sigue las instrucciones de la app y marca la parada como excepción en lugar de esperar en una escalera.",
              ],
            },
            {
              heading: "Si te sientes inseguro",
              paragraphs: [
                "Vete. Marca la parada como excepción en la app; nunca cuenta en tu contra. Reporta cualquier preocupación sobre una dirección en la app y Lista de Compras Plus se encarga: un Runner nunca confronta a nadie. En una emergencia, llama primero al 911, siempre.",
              ],
            },
          ],
          actions: [
            "Practica estacionar de forma que salgas de frente en tus próximas tres rutas.",
            "Usa un carrito para todo lo que pase de dos bolsas; ve Tu equipo.",
            "Ubica el campo de acceso al edificio en las notas de entrega para saber dónde buscar.",
          ],
        },
      },
    },
    {
      slug: "lifting-and-carrying",
      related: [
        "your-gear/coolers-and-bags",
        "your-gear/cart-and-winter-gear",
        "if-you-have-an-accident/incident-checklist",
      ],
      body: {
        en: {
          title: "Lifting and carrying",
          summary: "A grocery run is dozens of small lifts. Protect your back on every one.",
          sections: [
            {
              heading: "The basics",
              bullets: [
                "Bend your knees, keep the load close, lift with your legs.",
                "Don't twist while holding weight; turn your feet instead.",
                "Keep loads balanced — one bag in each hand beats three in one.",
                "Set bags on the trunk edge before you reach in.",
                "If it takes a grunt, it's two trips.",
              ],
            },
            {
              heading: "Heavy orders",
              paragraphs: [
                "Water cases, litter, pet food and bulk items are the usual back-breakers. Use a folding cart for any stop with more than a few bags. Ask the store for help loading a heavy case; most will. If a customer's stairs plus a heavy order feel like too much, leave the order where the notes allow and message them from the app.",
              ],
            },
            {
              heading: "Your car setup",
              paragraphs: [
                "Keep the trunk clear so bags sit at trunk-lip height, not on the floor. A crate or bin keeps bags upright and stops the sliding that leads to awkward reaches. Insulated bags with handles are easier on the wrists than plastic.",
              ],
            },
            {
              heading: "If you get hurt",
              paragraphs: [
                "For a serious injury, call 911 first, always. For anything else, stop, mark the stop as an exception in the app — it never counts against you — and report the incident from the run screen or the portal. Don't push through a back strain; one lost shift beats a lost month.",
              ],
            },
          ],
          actions: [
            "Get a folding cart if you don't have one.",
            "Set up a crate or bin in the trunk this week.",
            "Practice the knees-not-back lift on every stop for one full shift.",
          ],
        },
        es: {
          title: "Cargar y levantar peso",
          summary: "Una ruta de víveres son docenas de levantamientos pequeños. Cuida tu espalda en cada uno.",
          sections: [
            {
              heading: "Lo básico",
              bullets: [
                "Dobla las rodillas, mantén la carga cerca del cuerpo, levanta con las piernas.",
                "No gires el torso con peso en las manos; mueve los pies.",
                "Equilibra la carga: una bolsa en cada mano es mejor que tres en una.",
                "Apoya las bolsas en el borde de la cajuela antes de estirarte hacia adentro.",
                "Si te sale un quejido, son dos viajes.",
              ],
            },
            {
              heading: "Pedidos pesados",
              paragraphs: [
                "Paquetes de agua, arena para gatos, alimento para mascotas y productos a granel son los que más lastiman la espalda. Usa un carrito plegable en cualquier parada con más de unas pocas bolsas. Pide ayuda en la tienda para cargar un paquete pesado; casi siempre te la dan. Si las escaleras del cliente más un pedido pesado son demasiado, deja el pedido donde las notas lo permitan y escríbele desde la app.",
              ],
            },
            {
              heading: "Cómo acomodar el auto",
              paragraphs: [
                "Mantén la cajuela despejada para que las bolsas queden a la altura del borde, no en el piso. Una caja o contenedor mantiene las bolsas de pie y evita que se deslicen y te fuercen a estirarte mal. Las bolsas térmicas con asas cuidan más las muñecas que el plástico.",
              ],
            },
            {
              heading: "Si te lastimas",
              paragraphs: [
                "Si es una lesión seria, llama primero al 911, siempre. Para todo lo demás, detente, marca la parada como excepción en la app (nunca cuenta en tu contra) y reporta el incidente desde la pantalla de la ruta o el portal. No sigas con la espalda lastimada: perder un turno es mejor que perder un mes.",
              ],
            },
          ],
          actions: [
            "Consigue un carrito plegable si no tienes uno.",
            "Acomoda una caja o contenedor en la cajuela esta semana.",
            "Practica levantar con las rodillas y no con la espalda en cada parada de un turno completo.",
          ],
        },
      },
    },
    {
      slug: "severe-weather",
      related: [
        "your-gear/cart-and-winter-gear",
        "your-gear/coolers-and-bags",
        "reporting-a-problem/what-never-counts-against-you",
      ],
      body: {
        en: {
          title: "Severe weather",
          summary: "Rain, snow, heat and storms — and when the right call is to stop.",
          sections: [
            {
              heading: "Before the shift",
              paragraphs: [
                "Check the forecast when you set your week, not on the morning of. If the days ahead look bad, change your schedule in advance — that costs nothing, and nobody is counting on you yet.",
                "Once a delivery is on your schedule a household is counting on it, so the decision to sit out a day is one to make early. If conditions turn genuinely dangerous — ice, flooding, a severe thunderstorm or tornado warning — you are never expected to drive into them. Tell Grocery List Plus and your customer as early as you can, and see \"Your schedule is a promise.\"",
              ],
            },
            {
              heading: "Rain and snow",
              bullets: [
                "Slow down and leave more distance; a full trunk changes how the car stops.",
                "Clear every window completely before you move.",
                "Keep insulated bags closed — rain and snow reach cold items faster than you'd think.",
                "Watch for ice on steps and unshoveled walks; a customer's porch is where slips happen.",
                "Carry a towel and a dry pair of shoes.",
              ],
            },
            {
              heading: "Heat",
              paragraphs: [
                "Keep water in the car and drink at every stop. Park in shade when you can. Cold items go into coolers right away; heat shortens the safe window. Never leave a pet or child in a hot car — and if you see one in someone else's, call 911.",
              ],
            },
            {
              heading: "When it turns dangerous mid-run",
              paragraphs: [
                "If a warning is issued while you're out, get to shelter. Your safety is never the thing you trade. Leave any stop that's unsafe and mark it as an exception in the app — it never counts against you.",
                "Then tell Grocery List Plus the moment you are safe, so we can re-route your remaining stops and let those households know. Getting off the road in a real emergency is not a missed delivery; going quiet is. In any emergency, call 911 first, always.",
              ],
            },
          ],
          actions: [
            "Put a towel, water and an ice scraper or umbrella in the car this week.",
            "Turn on severe-weather alerts on your phone.",
            "Write down your personal no-go conditions (ice, flooding, warnings) so the decision is already made.",
          ],
        },
        es: {
          title: "Clima severo",
          summary: "Lluvia, nieve, calor y tormentas, y cuándo lo correcto es parar.",
          sections: [
            {
              heading: "Antes del turno",
              paragraphs: [
                "Revisa el pronóstico cuando armas tu semana, no la mañana del recorrido. Si los días que vienen se ven mal, cambia tu horario con anticipación — eso no cuesta nada y todavía nadie cuenta contigo.",
                "Una vez que una entrega está en tu horario, un hogar cuenta con ella, así que la decisión de no salir un día se toma temprano. Si las condiciones se vuelven realmente peligrosas (hielo, inundaciones, alertas de tormenta severa o tornado), nunca se espera que manejes en ellas. Avísale a Lista de Compras Plus y a tu cliente lo antes posible, y lee \"Tu horario es una promesa\".",
              ],
            },
            {
              heading: "Lluvia y nieve",
              bullets: [
                "Baja la velocidad y deja más distancia; una cajuela llena cambia cómo frena el auto.",
                "Limpia todas las ventanas por completo antes de moverte.",
                "Mantén cerradas las bolsas térmicas: la lluvia y la nieve llegan a los productos fríos más rápido de lo que crees.",
                "Cuidado con el hielo en escalones y banquetas sin despejar; el porche del cliente es donde ocurren los resbalones.",
                "Lleva una toalla y un par de zapatos secos.",
              ],
            },
            {
              heading: "Calor",
              paragraphs: [
                "Ten agua en el auto y bebe en cada parada. Estaciona a la sombra cuando puedas. Los productos fríos van a la hielera de inmediato; el calor acorta el tiempo seguro. Nunca dejes a una mascota o a un niño en un auto caliente, y si ves uno en el auto de alguien más, llama al 911.",
              ],
            },
            {
              heading: "Cuando se pone peligroso a media ruta",
              paragraphs: [
                "Si se emite una alerta mientras estás afuera, ponte a resguardo. Tu seguridad nunca es lo que se negocia. Deja cualquier parada que no sea segura y márcala como excepción en la app: eso nunca cuenta en tu contra.",
                "Después avísale a Lista de Compras Plus en cuanto estés a salvo, para que podamos redirigir tus paradas restantes y avisarles a esos hogares. Salir de la carretera en una emergencia real no es una entrega perdida; quedarte callado sí lo es. En cualquier emergencia, llama al 911 primero, siempre.",
              ],
            },
          ],
          actions: [
            "Pon una toalla, agua y un raspador de hielo o un paraguas en el auto esta semana.",
            "Activa las alertas de clima severo en tu teléfono.",
            "Anota tus condiciones personales de no salir (hielo, inundaciones, alertas) para que la decisión ya esté tomada.",
          ],
        },
      },
    },
  ],
};
